import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiWifi, FiCoffee, FiCar, FiWind, FiDroplet, FiTv, FiEye, FiLeaf } = FiIcons;

const Accommodation = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const features = [
    { icon: FiWind, label: "Air Conditioning", description: "Cool comfort after hot safari days" },
    { icon: FiDroplet, label: "Hot Water", description: "Private bathroom with 24/7 hot water" },
    { icon: FiEye, label: "River View", description: "Private balcony overlooking Walawe River" },
    { icon: FiCar, label: "Free Parking", description: "Secure parking for your vehicle" },
    { icon: FiWifi, label: "Lobby WiFi", description: "Stay connected in our lounge area" },
    { icon: FiCoffee, label: "Complimentary Tea", description: "Fresh Ceylon tea & coffee" },
    { icon: FiTv, label: "Entertainment", description: "TV with local & satellite channels" },
    { icon: FiLeaf, label: "Garden Lounge", description: "Peaceful outdoor relaxation area" }
  ];

  const roomImages = [
    {
      src: "https://images.unsplash.com/photo-1590490360182-c33d57733427?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      alt: "Comfortable bedroom with river view",
      title: "Comfortable Rooms"
    },
    {
      src: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      alt: "Private balcony overlooking nature",
      title: "Private Balcony"
    },
    {
      src: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      alt: "Garden lounge area",
      title: "Garden Lounge"
    }
  ];

  return (
    <section id="rooms" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Your <span className="gradient-text">Base Camp</span>
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Clean, comfortable air-conditioned rooms with private balconies overlooking the Walawe River. 
            The perfect place to rest and refresh between safari adventures.
          </p>
        </motion.div>

        {/* Image Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.2 }}
          className="grid md:grid-cols-3 gap-6 mb-16"
        >
          {roomImages.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="relative group"
            >
              <div className="glass-card p-3 rounded-2xl">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-64 object-cover rounded-xl"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex items-end p-6">
                  <h3 className="text-white font-semibold text-lg">
                    {image.title}
                  </h3>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.4 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="glass-card p-6 rounded-2xl text-center"
            >
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="inline-block p-3 rounded-xl bg-gradient-to-r from-blue-400 to-purple-500 mb-4"
              >
                <SafeIcon icon={feature.icon} className="text-2xl text-white" />
              </motion.div>
              
              <h3 className="text-white font-semibold mb-2">
                {feature.label}
              </h3>
              
              <p className="text-white/70 text-sm">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Room Description */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.6 }}
          className="glass-morphism rounded-3xl p-8 lg:p-12"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-white mb-6">
                Comfort Meets <span className="gradient-text">Nature</span>
              </h3>
              
              <div className="space-y-4 text-white/80">
                <p>
                  Our thoughtfully designed rooms provide the perfect sanctuary after your wildlife adventures. 
                  Each room features modern amenities while maintaining the authentic charm of rural Sri Lanka.
                </p>
                
                <p>
                  Wake up to the sounds of nature and enjoy your morning tea on your private balcony, 
                  watching the mist rise from the Walawe River as the forest comes alive.
                </p>
                
                <p>
                  We've upgraded our housekeeping protocols for 2025, ensuring professional laundry 
                  services and pristine linens for every guest.
                </p>
              </div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                className="mt-8"
              >
                <a
                  href="https://wa.me/94771234567?text=Hi%20Mohan,%20I%20would%20like%20to%20see%20more%20photos%20of%20the%20rooms"
                  className="inline-block glass-card px-8 py-4 rounded-full text-white font-semibold hover:bg-white/20 transition-all duration-300"
                >
                  View More Photos
                </a>
              </motion.div>
            </div>

            <div className="relative">
              <motion.img
                whileHover={{ scale: 1.02 }}
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Cozy hotel room interior"
                className="w-full h-80 object-cover rounded-2xl"
              />
              
              <motion.div
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 1 }}
                className="absolute -bottom-4 -right-4 glass-morphism p-4 rounded-full"
              >
                <SafeIcon icon={FiEye} className="text-2xl gradient-text" />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Accommodation;